import React, { useState, useRef, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { Document, Page, pdfjs } from 'react-pdf';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import styles from './styles/styles.module.css';
import { FaFileSignature } from 'react-icons/fa';
import { PiTextboxFill } from 'react-icons/pi';
import { BsFillCalendarDateFill } from 'react-icons/bs';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface SignatureModalProps {
  onHide: () => void;
  file: File | null;
  setFormData: any;
}

type FieldType = 'signature' | 'initials' | 'textbox' | 'date';

interface Field {
  id: number;

  // Top-left position in our 595-wide coordinate space
  x: number;
  y: number;

  width: number;
  height: number;

  page: number;
  type: FieldType;
  text?: string;
}

const SignatureModal: React.FC<SignatureModalProps> = ({
  setFormData,
  onHide,
  file,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [fields, setFields] = useState<Field[]>([]);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);

  // We'll store which FieldType is currently selected in a ref
  const selectedFieldType = useRef<FieldType | null>(null);

  // Toggle states for pressed styling
  const [signatureOn, setSignatureOn] = useState(false);
  const [textOn, setTextOn] = useState(false);
  const [dateOn, setDateOn] = useState(false);

  // This state controls the signature submenu (choose between "Signature" or "Initials")
  const [showSignatureMenu, setShowSignatureMenu] = useState(false);

  // Scroll to bottom of PDF container whenever the signature tool toggles on
  useEffect(() => {
    if (pdfContainerRef.current && signatureOn) {
      pdfContainerRef.current.scrollTo({
        top: pdfContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [signatureOn]);

  // Immediately show an alert if the file is not PDF
  if (file && file.type !== 'application/pdf') {
    return (
      <Modal show={true} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Invalid file type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please select a PDF file.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const getFieldDimensions = (type: FieldType) => {
    switch (type) {
      case 'signature':
        return {
          width: 160,
          height: 50,
        };

      case 'initials':
        return {
          width: 80,
          height: 40,
        };

      case 'date':
        return {
          width: 110,
          height: 40,
        };

      case 'textbox':
      default:
        return {
          width: 180,
          height: 40,
        };
    }
  };

  const getFieldColor = (type: FieldType) => {
    switch (type) {
      case 'signature':
        return 'rgba(0, 128, 0, 0.5)';
      case 'initials':
        return 'rgba(0, 0, 255, 0.5)';
      case 'date':
        return 'rgba(255, 0, 0, 0.5)';
      case 'textbox':
      default:
        return 'rgba(255, 165, 0, 0.5)';
    }
  };

  const onDragStop = (
    event: DraggableEvent,
    data: DraggableData,
    fieldId: number
  ) => {
    setFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId ? { ...field, x: data.x, y: data.y } : field
      )
    );
  };

  // Add new field on click if a field type is selected
  const addField = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!pdfContainerRef.current || !selectedFieldType.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    let defaultText: string | undefined;
    switch (selectedFieldType.current) {
      case 'textbox':
        defaultText = 'Enter text...';
        break;
      case 'date':
        defaultText = 'Date';
        break;
      case 'signature':
        defaultText = 'Sign Here';
        break;
      case 'initials':
        defaultText = 'Initials';
        break;
    }

    const dimensions = getFieldDimensions(selectedFieldType.current!);

    const width = dimensions.width;
    const height = dimensions.height;

    // Center the new field where the admin clicked,
    // but STORE its top-left coordinates.
    const x = Math.max(0, Math.min(clickX - width / 2, rect.width - width));

    const y = Math.max(0, Math.min(clickY - height / 2, rect.height - height));

    setFields((prev) => [
      ...prev,
      {
        id: Date.now(),

        x,
        y,

        width,
        height,

        page: currentPage,

        type: selectedFieldType.current!,

        text: defaultText,
      },
    ]);

    // Clear the selection after adding a field so we don't place duplicates
    selectedFieldType.current = null;
  };

  // Called by "Done" button to send data to the backend
  const sendToBackend = async () => {
    if (!file) {
      alert('No PDF file selected!');
      return;
    }

    const formData = new FormData();

    formData.append('pdf', file);

    formData.append(
      'fields',
      JSON.stringify({
        schemaVersion: 1,

        coordinateSpace: {
          baseWidth: 595,
          origin: 'top-left',
          fieldAnchor: 'top-left',
        },

        fields,
      })
    );

    setFormData(formData);
  };

  // Reset all toggles to false; set the one we want to true
  const resetTogglesExcept = (tool: 'signature' | 'textbox' | 'date') => {
    setSignatureOn(tool === 'signature');
    setTextOn(tool === 'textbox');
    setDateOn(tool === 'date');
  };

  // Handle clicking the signature icon (shows or hides the sub-menu)
  const handleSignatureIconClick = () => {
    // If we click the signature icon again, maybe we toggle the menu off
    // Or we ensure it's open – depends on your preferred UX.
    // For now let's just open or close it.
    setShowSignatureMenu((prev) => !prev);

    // Make signatureOn = true, the rest off
    resetTogglesExcept('signature');

    // We haven't actually picked a final field type yet:
    // We'll do that once the user chooses from the sub-menu.
    selectedFieldType.current = null;
  };

  // Handle picking "Signature" or "Initials" from the sub-menu
  const handleSignatureChoice = (choice: FieldType) => {
    selectedFieldType.current = choice;
    setShowSignatureMenu(false);
  };

  // Handle the text icon
  const handleTextIconClick = () => {
    resetTogglesExcept('textbox');
    // Hide signature menu if open
    setShowSignatureMenu(false);
    selectedFieldType.current = 'textbox';
  };

  // Handle the date icon
  const handleDateIconClick = () => {
    resetTogglesExcept('date');
    // Hide signature menu if open
    setShowSignatureMenu(false);
    selectedFieldType.current = 'date';
  };

  return (
    <Modal show={true} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Signature, Initials, Text, or Date</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {file ? (
          <div ref={pdfContainerRef} className={styles.pdfContainer}>
            <Document
              file={URL.createObjectURL(file)}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              loading={
                <div className={styles.pleaseWaitWrapper}>Please wait...</div>
              }
            >
              <Page
                pageNumber={currentPage}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                width={595} // A4 width at ~72 DPI
              />
            </Document>

            {/* Overlay for adding and dragging fields */}
            <div className={styles.overlay} onClick={addField}>
              {fields
                .filter((field) => field.page === currentPage)
                .map((field) => (
                  <Draggable
                    key={field.id}
                    bounds="parent"
                    position={{ x: field.x, y: field.y }}
                    onStop={(e, data) => onDragStop(e, data, field.id)}
                  >
                    <div
                      style={{
                        position: 'absolute',

                        top: 0,
                        left: 0,

                        width: `${field.width}px`,
                        height: `${field.height}px`,

                        boxSizing: 'border-box',

                        backgroundColor: getFieldColor(field.type),

                        border: '2px dashed black',

                        color: '#fff',
                        cursor: 'move',
                        fontSize: '14px',
                        textAlign: 'center',

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={(ev) => ev.stopPropagation()} // Prevent addField from firing inside the box
                    >
                      {/* If it's a textbox, show an input */}
                      {field.type === 'textbox' ? (
                        <input
                          type="text"
                          value={field.text || ''}
                          onChange={(ev) =>
                            setFields((prevFields) =>
                              prevFields.map((f) =>
                                f.id === field.id
                                  ? { ...f, text: ev.target.value }
                                  : f
                              )
                            )
                          }
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#000',
                            outline: 'none',
                            width: '100%',
                            textAlign: 'center',
                          }}
                        />
                      ) : (
                        // Otherwise, just render the stored text
                        field.text
                      )}
                    </div>
                  </Draggable>
                ))}
            </div>
          </div>
        ) : (
          <p>No valid file uploaded.</p>
        )}

        {/* 
          Main tool icons 
          Note how we use handleSignatureIconClick for the signature icon
          so we can show a sub-menu for picking "signature" or "initials."
        */}
        <div className={styles.controls}>
          {/* Signature icon */}
          <div
            onClick={handleSignatureIconClick}
            className={signatureOn ? styles.wysiwygON : styles.wysiwyg}
          >
            <FaFileSignature color={'white'} size={28} />
          </div>

          {/* Submenu for Signature/Initials if showSignatureMenu == true */}
          {showSignatureMenu && (
            <div className={styles.signatureSubmenu}>
              <button onClick={() => handleSignatureChoice('signature')}>
                Signature
              </button>
              <button onClick={() => handleSignatureChoice('initials')}>
                Initials
              </button>
            </div>
          )}

          {/* Text icon */}
          <div
            className={textOn ? styles.wysiwygON : styles.wysiwyg}
            onClick={handleTextIconClick}
          >
            <PiTextboxFill color={'white'} size={28} />
          </div>

          {/* Date icon */}
          <div
            className={dateOn ? styles.wysiwygON : styles.wysiwyg}
            onClick={handleDateIconClick}
          >
            <BsFillCalendarDateFill color={'white'} size={28} />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          Previous
        </Button>
        <Button
          disabled={currentPage >= (numPages ?? 0)}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </Button>

        <Button
          className={styles.submitButton}
          variant="primary"
          onClick={sendToBackend}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignatureModal;
